import { map, groupBy, orderBy } from "lodash";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { formatDistanceToNow } from "date-fns";

import {
  Avatar,
  Breadcrumbs as MuiBreadcrumbs,
  Button,
  Card as MuiCard,
  CardActions,
  CardContent as MuiCardContent,
  Chip as MuiChip,
  Divider as MuiDivider,
  Grid,
  Link,
  Typography as MuiTypography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  InputLabel,
  FormControl,
  IconButton,
  CircularProgress,
  Tabs,
  Tab,
} from "@mui/material";
import { Create, Delete, OpenInNew } from "@mui/icons-material";
import { Formik, Form } from "formik";
import { AvatarGroup as MuiAvatarGroup } from "@mui/material";
import { spacing } from "@mui/system";
import useAuth from "../../hooks/useAuth";

import {
  queryProjects,
  addProject,
  updateProject,
  deleteProject,
} from "../../redux/slices/projects";

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Card = styled(MuiCard)(spacing);
const Divider = styled(MuiDivider)(spacing);
const Typography = styled(MuiTypography)(spacing);

const CardContent = styled(MuiCardContent)`
  border-bottom: 1px solid ${(props) => props.theme.palette.grey[300]};
`;

const Chip = styled(MuiChip)`
  height: 20px;
  padding: 4px 0;
  font-size: 85%;
  background-color: ${(props) =>
    props.theme.palette[props.color ? props.color : "primary"].light};
  color: ${(props) => props.theme.palette.common.white};
  margin-bottom: ${(props) => props.theme.spacing(2)};
  margin-right: ${(props) => props.theme.spacing(1)};
`;

const LabelChip = styled(MuiChip)`
  height: 18px;
  font-size: 75%;
  margin-right: 4px;
  margin-bottom: 4px;
`;

const AvatarGroup = styled(MuiAvatarGroup)`
  margin-left: ${(props) => props.theme.spacing(2)};
`;

const ProjectCard = ({ project, isAdmin, onEdit, onDelete }) => {
  const {
    title,
    description,
    status,
    labels,
    members,
    due,
    url,
    dateLastActivity,
  } = project;

  const getStatusColor = (status) => {
    switch (status) {
      case "Not Started":
        return "default";
      case "Up Next":
        return "info";
      case "In Progress":
        return "warning";
      case "Completed":
        return "success";
      default:
        return "primary";
    }
  };

  const getLabelColor = (color) => {
    const colorMap = {
      green: "success",
      yellow: "warning",
      orange: "warning",
      red: "error",
      purple: "secondary",
      blue: "info",
      sky: "info",
      lime: "success",
      pink: "error",
      black: "default",
    };
    return colorMap[color] || "default";
  };

  const getInitials = (name) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ position: "relative" }}>
          <Typography gutterBottom variant="h5" component="h2">
            {title}
          </Typography>
          {isAdmin && (
            <Box sx={{ position: "absolute", top: 0, right: 0 }}>
              <IconButton size="small" onClick={onEdit}>
                <Create fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={onDelete} color="error">
                <Delete fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>

        <Box sx={{ mb: 2 }}>
          <Chip label={status} color={getStatusColor(status)} size="small" />
          {labels.map((label, i) => (
            <LabelChip
              key={i}
              label={label.name || label.color}
              color={getLabelColor(label.color)}
              size="small"
            />
          ))}
        </Box>

        <Typography
          mb={2}
          color="textSecondary"
          component="p"
          sx={{ minHeight: 40 }}
        >
          {description || "No description"}
        </Typography>

        {due && (
          <Typography
            variant="caption"
            color="textSecondary"
            display="block"
            mb={1}
          >
            Due: {new Date(due).toLocaleDateString()}
          </Typography>
        )}

        {dateLastActivity && (
          <Typography
            variant="caption"
            color="textSecondary"
            display="block"
            mb={2}
          >
            Updated {formatDistanceToNow(new Date(dateLastActivity))} ago
          </Typography>
        )}

        {members.length > 0 && (
          <AvatarGroup max={4}>
            {members.map((member, i) => (
              <Avatar
                key={i}
                sx={{ width: 28, height: 28, fontSize: 12 }}
                title={member.fullName}
              >
                {member.initials || getInitials(member.fullName)}
              </Avatar>
            ))}
          </AvatarGroup>
        )}
      </CardContent>
      <CardActions>
        {url && (
          <Button
            size="small"
            color="primary"
            href={url}
            target="_blank"
            startIcon={<OpenInNew fontSize="small" />}
          >
            Open in Trello
          </Button>
        )}
      </CardActions>
    </Card>
  );
};

const ProjectDialog = ({ open, onClose, editProject, onSubmit }) => {
  const isEdit = Boolean(editProject);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Formik
        enableReinitialize
        initialValues={
          editProject || {
            title: "",
            description: "",
            status: "Not Started",
          }
        }
        onSubmit={(values, { setSubmitting, resetForm }) => {
          onSubmit(values, () => {
            setSubmitting(false);
            resetForm();
            onClose();
          });
        }}
      >
        {({ isSubmitting, handleChange, values }) => (
          <Form>
            <DialogTitle>{isEdit ? "Edit Project" : "Add Project"}</DialogTitle>
            <DialogContent>
              <TextField
                name="title"
                value={values.title}
                label="Title"
                margin="normal"
                fullWidth
                required
                onChange={handleChange}
              />
              <TextField
                name="description"
                value={values.description}
                label="Description"
                margin="normal"
                fullWidth
                multiline
                rows={4}
                onChange={handleChange}
              />
              <FormControl fullWidth margin="normal">
                <TextField
                  select
                  name="status"
                  label="Status"
                  onChange={handleChange}
                  value={values.status}
                >
                  <MenuItem value="Not Started">Not Started (Backlog)</MenuItem>
                  <MenuItem value="Up Next">Up Next</MenuItem>
                  <MenuItem value="In Progress">In Progress</MenuItem>
                  <MenuItem value="Completed">Done</MenuItem>
                </TextField>
              </FormControl>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose}>Cancel</Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isSubmitting}
              >
                {isEdit ? "Update" : "Create"}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

function Projects() {
  const {
    user: { isAdmin },
  } = useAuth();
  const dispatch = useDispatch();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editProject, setEditProject] = React.useState(null);
  const [tabValue, setTabValue] = React.useState(0);

  const { projects, loading, error } = useSelector((state) => state.projects);

  React.useEffect(() => {
    dispatch(queryProjects());
  }, [dispatch]);

  const handleOpenDialog = (project = null) => {
    setEditProject(project);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditProject(null);
  };

  const handleSubmit = (values, cb) => {
    if (editProject) {
      dispatch(updateProject({ id: editProject.id, values, cb }));
    } else {
      dispatch(addProject({ ...values, cb }));
    }
  };

  const handleDelete = (project) => {
    if (window.confirm(`Delete "${project.title}"?`)) {
      dispatch(deleteProject({ id: project.id }));
    }
  };

  // Group projects by status
  const projectsByStatus = groupBy(Object.values(projects), "status");
  const statuses = ["Not Started", "Up Next", "In Progress", "Completed"];

  // Filter for tabs
  const filteredProjects =
    tabValue === 0
      ? Object.values(projects)
      : Object.values(projects).filter(
          (p) => p.status === statuses[tabValue - 1]
        );

  return (
    <React.Fragment>
      <Helmet title="Projects" />

      <Box sx={{ position: "relative" }}>
        <Typography variant="h3" gutterBottom display="inline">
          Projects
        </Typography>
        {isAdmin && (
          <Button
            variant="contained"
            onClick={() => handleOpenDialog()}
            sx={{ position: "absolute", right: 0, top: 0 }}
          >
            Create Project
          </Button>
        )}
      </Box>

      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} to="/">
          Dashboard
        </Link>
        <Typography>Projects</Typography>
      </Breadcrumbs>

      <Divider my={4} />

      {/* Status Tabs */}
      <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 4 }}>
        <Tab label={`All (${Object.keys(projects).length})`} />
        {statuses.map((status, i) => (
          <Tab
            key={status}
            label={`${status} (${(projectsByStatus[status] || []).length})`}
          />
        ))}
      </Tabs>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Typography color="error" sx={{ py: 4 }}>
          Error loading projects: {error}
        </Typography>
      )}

      {!loading && !error && (
        <Grid container spacing={4}>
          {orderBy(filteredProjects, ["dateLastActivity"], ["desc"]).map(
            (project) => (
              <Grid item xs={12} sm={6} lg={4} xl={3} key={project.id}>
                <ProjectCard
                  project={project}
                  isAdmin={isAdmin}
                  onEdit={() => handleOpenDialog(project)}
                  onDelete={() => handleDelete(project)}
                />
              </Grid>
            )
          )}
          {filteredProjects.length === 0 && (
            <Grid item xs={12}>
              <Typography color="textSecondary" align="center" py={4}>
                No projects in this category
              </Typography>
            </Grid>
          )}
        </Grid>
      )}

      <ProjectDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        editProject={editProject}
        onSubmit={handleSubmit}
      />
    </React.Fragment>
  );
}

export default Projects;
